import { queryClient } from '@/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  type ConversationAgentRun,
  ConversationAgentRunSchema,
  type ConversationAgentRuns,
  type ConversationAgentRunsQuery,
  ConversationAgentRunsSchema,
  type CreateConversationAgentRun,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const useConversationAgentRunsQuery = (
  params: ConversationAgentRunsQuery,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['conversation-agent-runs', params],
    queryFn: async () => {
      const response = await apiClient.get<ConversationAgentRuns>(
        '/conversation-agent-runs',
        { params },
      );
      const validatedData = ConversationAgentRunsSchema.safeParse(
        response.data,
      );
      if (!validatedData.success) {
        logger.warn('failed to validate conversation agent runs data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const useConversationAgentRunQuery = (
  conversationAgentRunId: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['conversation-agent-run', conversationAgentRunId],
    queryFn: async () => {
      const response = await apiClient.get<ConversationAgentRun>(
        `/conversation-agent-runs/${conversationAgentRunId}`,
      );
      const validatedData = ConversationAgentRunSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate conversation agent run data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const createConversationAgentRun = async (
  data: CreateConversationAgentRun,
) => {
  const response = await apiClient.post<ConversationAgentRun>(
    '/conversation-agent-runs',
    data,
  );
  const validatedData = ConversationAgentRunSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate conversation agent run data', {
      error: validatedData.error,
    });
    return response.data;
  }
  await queryClient.invalidateQueries({
    queryKey: [
      'conversation-agent-runs',
      { conversationIds: [data.conversationId] },
    ],
  });
  await queryClient.invalidateQueries({
    queryKey: ['conversation-agent-runs', { agentRunIds: [data.agentRunId] }],
  });
  return validatedData.data;
};

export const useCreateConversationAgentRunMutation = () => {
  return useMutation({
    mutationFn: createConversationAgentRun,
  });
};
